"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import RichText from "@/components/RichText";
import { cn } from "@/utilities/core/cn";
import { useLabels } from "@/lib/labels/LabelsProvider";
import type { ConsentDef } from "./types";

export interface ConsentCheckboxListProps {
    consents: ConsentDef[];
    /** consentIds the user has already accepted (rendered checked + disabled). */
    initialAccepted?: Set<string>;
    /** Notified whenever the set of currently-ticked consentIds changes. */
    onChange: (acceptedIds: string[]) => void;
}

/**
 * Pure presentation primitive: renders one checkbox per consent + the rich-text body.
 *
 * - Required consents start unchecked unless already accepted; if already accepted they
 *   render checked and disabled.
 * - Optional consents honor `defaultChecked` initially; once already accepted, they
 *   render checked and disabled (withdrawal happens in the profile settings, not here).
 *
 * State is initialized from props and re-syncs only when the *content* of `consents`
 * or `initialAccepted` changes (not on every render — callers may pass freshly-derived
 * arrays/sets each render).
 */
export function ConsentCheckboxList({ consents, initialAccepted, onChange }: ConsentCheckboxListProps) {
    const labels = useLabels();
    const t = (labels as { profileConsent?: Record<string, string> }).profileConsent ?? {};
    // Stable signatures: change only when actual contents change.
    const consentsKey = useMemo(
        () => consents.map((c) => `${c.consentId}:${c.required ? 1 : 0}:${c.defaultChecked ? 1 : 0}`).join("|"),
        [consents]
    );
    const acceptedKey = useMemo(() => {
        if (!initialAccepted) return "";
        return [...initialAccepted].sort().join(",");
    }, [initialAccepted]);

    const buildInitialState = useMemo(() => {
        return () => {
            const map: Record<string, boolean> = {};
            const accepted = initialAccepted ?? new Set<string>();
            for (const c of consents) {
                if (accepted.has(c.consentId)) {
                    map[c.consentId] = true;
                } else if (c.required) {
                    map[c.consentId] = false;
                } else {
                    map[c.consentId] = c.defaultChecked;
                }
            }
            return map;
        };
        // We intentionally rebuild only when the content keys change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consentsKey, acceptedKey]);

    const [checked, setChecked] = useState<Record<string, boolean>>(buildInitialState);

    // Re-sync when the underlying content actually changes (not on every render).
    const prevKeyRef = useRef<string>(`${consentsKey}::${acceptedKey}`);
    useEffect(() => {
        const key = `${consentsKey}::${acceptedKey}`;
        if (prevKeyRef.current !== key) {
            prevKeyRef.current = key;
            setChecked(buildInitialState());
        }
    }, [consentsKey, acceptedKey, buildInitialState]);

    // Notify parent when the ticked set changes.
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);
    useEffect(() => {
        const ids = Object.entries(checked)
            .filter(([, v]) => v)
            .map(([id]) => id);
        onChangeRef.current(ids);
    }, [checked]);

    if (consents.length === 0) return null;

    const accepted = initialAccepted ?? new Set<string>();

    return (
        <div className="space-y-2">
            {consents.map((c) => {
                const isAlreadyAccepted = accepted.has(c.consentId);
                const isChecked = !!checked[c.consentId];
                return (
                    <label
                        key={c.consentId}
                        className={cn(
                            "group block cursor-pointer rounded-lg border px-3.5 py-3 transition-colors",
                            "border-border/60 hover:bg-muted/30 has-[:checked]:border-primary/40",
                            isAlreadyAccepted && "cursor-default border-border/40 bg-muted/20 hover:bg-muted/20"
                        )}
                        data-required={c.required ? "true" : "false"}
                        data-consent-id={c.consentId}
                    >
                        <div className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                className="h-4 w-4 shrink-0 cursor-pointer rounded accent-primary disabled:cursor-not-allowed"
                                checked={isChecked}
                                disabled={isAlreadyAccepted}
                                onChange={(e) =>
                                    setChecked((prev) => ({ ...prev, [c.consentId]: e.target.checked }))
                                }
                            />
                            <div
                                className={cn(
                                    "min-w-0 flex-1 text-sm text-foreground",
                                    "[&_a]:text-primary [&_a]:underline",
                                    "[&>*+*]:mt-1.5"
                                )}
                            >
                                <RichText content={c.body} />
                            </div>
                            <div className="ml-1 shrink-0">
                                {isAlreadyAccepted ? (
                                    <Check className="h-3.5 w-3.5 text-primary/70" aria-hidden="true" />
                                ) : c.required ? (
                                    <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                        {t.required || "Required"}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </label>
                );
            })}
        </div>
    );
}
